<template>
    <section class="contacts-section-wrapper">
        <div class="section-header">
            <h4>{{ $t("message.contacts") }}</h4>
        </div>

        <div v-if="contacts.length" class="contacts-section">
            <div v-for="contact in contacts" :key="contact.id" class="contact-row">
                <div class="contact-info">
                    <div class="contact-name">
                        <i class="material-icons">account_circle</i>
                        <span>{{ contact.Full_Name }}</span>
                    </div>
                    <div class="contact-email" v-if="contact.Email">
                        <i class="material-icons">email</i>
                        <span>{{ contact.Email }}</span>
                    </div>
                    <div class="contact-phone" v-if="contact.Phone || contact.Mobile">
                        <i class="material-icons">phone</i>
                        <span>{{ contact.Phone || contact.Mobile }}</span>
                    </div>
                    <div class="contact-footer">
                        <div class="contact-owner">
                            <i class="material-icons">person</i>
                            <span>{{ $_.get(contact, "Owner.name", "") }}</span>
                        </div>
                        <div class="contact-account" v-if="$_.get(contact, 'Account_Name.name')">
                            <i class="material-icons">business</i>
                            <span>{{ $_.get(contact, "Account_Name.name", "") }}</span>
                        </div>
                    </div>
                    <div class="contact-layout">
                        <i class="material-icons">category</i>
                        <span>{{ $_.get(contact, "Layout.name", "Default Layout") }}</span>
                    </div>
                    <div class="contact-date">
                        <i class="material-icons">event</i>
                        <span>{{ formatDate(contact.Created_Time) }}</span>
                    </div>
                </div>
                <SupsisButton class="view-contact-btn" icon="open_in_new" @click="gotoContactOnZoho(contact.id)" />
                <a-divider />
            </div>
        </div>

        <div v-else-if="loading" class="loading-container">
            <span>{{ $t("message.loadingContacts") }}</span>
            <BucketSpinner class="large-spinner" />
        </div>

        <div v-else class="empty-contacts-container">
            <span class="material-icons empty-icon">contacts</span>
            <p class="empty-text">{{ $t("message.noContactsFound") }}</p>
        </div>
    </section>
</template>

<script>
import Vue from "vue";
import { ZOHO_API } from "../../../../api/zoho";
import { mapState } from "vuex";
import BucketSpinner from "../../../../views/Reports/common/spinners/BucketSpinner.vue";
import SupsisButton from "../../../../views/Pages/Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "ZohoContactsSection",
    components: {
        BucketSpinner,
        SupsisButton,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState({
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho" && !!x.isDefault),
            zohoChannel: (state) => state.channel.channels.find((x) => x.type === "zoho" && !!x.isDefault),
        }),
    },
    data() {
        return {
            contacts: [],
            loading: false,
        };
    },
    async created() {
        await this.fetchContacts();
    },
    methods: {
        async fetchContacts() {
            this.loading = true;
            try {
                const contactId = this.contact._id;
                const phone = this.contact.whatsappNumber || this.contact.phone;
                const contacts = await ZOHO_API.searchRecordsByPhone("Contacts", phone);
                if (this.contact?._id === contactId) {
                    this.contacts = Array.isArray(contacts) ? contacts : [];
                }
            } catch (error) {
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return "";
            return new Date(dateString).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        gotoContactOnZoho(contactId) {
            const orgId = _.get(this.zohoChannel, "payload.organization.domain_name");
            if (orgId && contactId) {
                window.open(`https://crm.zoho.com/crm/${orgId}/tab/Contacts/${contactId}`, "_blank");
            }
        },
    },
    watch: {
        contact: {
            handler() {
                this.contacts = [];
                this.fetchContacts();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.contacts-section-wrapper {
    .section-header {
        margin-bottom: 12px;

        h4 {
            font-size: 16px;
            font-weight: 500;
            color: #4a5568;
            margin: 0;
        }
    }

    .contacts-section {
        .contact-row {
            position: relative;
            padding: 12px;

            .contact-info {
                .contact-name,
                .contact-email,
                .contact-phone,
                .contact-date,
                .contact-layout,
                .contact-footer > div {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 4px;

                    i.material-icons {
                        font-size: 18px;
                        color: #64748b;
                    }
                }

                .contact-name {
                    font-weight: 500;
                    font-size: 15px;
                    color: #2d3748;
                }

                .contact-email,
                .contact-phone {
                    font-size: 0.9em;
                    color: #718096;
                }

                .contact-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.9em;

                    .contact-owner,
                    .contact-account {
                        color: #718096;
                    }
                }

                .contact-layout {
                    font-size: 0.85em;
                    color: #94a3b8;
                }

                .contact-date {
                    font-size: 0.85em;
                    color: #94a3b8;
                }
            }

            .view-contact-btn {
                position: absolute;
                top: 12px;
                right: 12px;
            }
        }
    }

    .loading-container,
    .empty-contacts-container {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 20px;
        color: #94a3b8;

        .empty-icon {
            font-size: 36px;
            margin-bottom: 8px;
        }

        .empty-text {
            font-size: 14px;
            text-align: center;
            margin: 0;
        }
    }
}
</style>

